import type {
  BrandAssetBackground,
  DesignSystemImportChangeDto,
  DesignSystemImportLogoChoice,
  DesignSystemImportLogoDto,
  KnowledgeBaseFileViewDto,
} from '@/types/knowledgeBase'
import { MARK_SLOTS, slotPhrase, type MarkType } from './brandLogos'

/**
 * Importing a design system into Brand Guidelines (ORB-1028). The API reads the zip and says what importing it would
 * change, saving nothing; a person then chooses where each logo in it goes, starting from the API's guess, and imports.
 */

/** Where a person puts a logo found in the design system: a mark's slot, or nowhere. */
export interface LogoChoice {
  type: MarkType | null
  /** Which backgrounds it is for; null is either. */
  background: BrandAssetBackground | null
}

/** A logo starts where the API guesses it goes. A guess of anything but a mark leaves it out until a person says. */
export function suggestedChoice(logo: DesignSystemImportLogoDto): LogoChoice {
  const type = MARK_SLOTS.find(slot => slot.type === logo.SuggestedAssetType)?.type ?? null
  return { type, background: type === null ? null : logo.SuggestedBackground ?? null }
}

/** The import's Choices, one per logo found, in the shape the API reads: a null AssetType leaves the file out. */
export function logoChoices(logos: readonly DesignSystemImportLogoDto[], choices: Record<string, LogoChoice>): DesignSystemImportLogoChoice[] {
  return logos.map(logo => {
    const choice = choices[logo.Path] ?? suggestedChoice(logo)
    return { Path: logo.Path, AssetType: choice.type, Background: choice.type === null ? null : choice.background }
  })
}

function slotOf(choice: LogoChoice): string | null {
  return choice.type === null ? null : `${choice.type}:${choice.background ?? 'Either'}`
}

/**
 * The slots two or more logos are chosen for. A slot holds one file, so the import would keep only one of them: the
 * person chooses which before importing.
 */
export function clashingSlots(logos: readonly DesignSystemImportLogoDto[], choices: Record<string, LogoChoice>): string[] {
  const counts = new Map<string, { choice: LogoChoice; count: number }>()
  for (const logo of logos) {
    const choice = choices[logo.Path] ?? suggestedChoice(logo)
    const slot = slotOf(choice)
    if (slot === null) continue
    const seen = counts.get(slot)
    counts.set(slot, { choice, count: (seen?.count ?? 0) + 1 })
  }
  return [...counts.values()]
    .filter(({ count }) => count > 1)
    .map(({ choice }) => slotPhrase(MARK_SLOTS.find(slot => slot.type === choice.type)!, 'the', choice.background))
}

/** The ground a logo found is shown on: dark only when it is chosen for dark backgrounds, light for either or none. */
export function previewGround(choice: LogoChoice): BrandAssetBackground {
  return choice.type !== null && choice.background === 'Dark' ? 'Dark' : 'Light'
}

/** The file a chosen logo replaces at this level: the one filling the same slot, the same type and background. */
export function replacedFile(files: readonly KnowledgeBaseFileViewDto[], choice: LogoChoice): KnowledgeBaseFileViewDto | null {
  if (choice.type === null) return null
  return files.find(file => file.AssetType === choice.type && (file.Background ?? null) === choice.background) ?? null
}

export interface ChangeGroup {
  family: string
  changes: DesignSystemImportChangeDto[]
  /** "3 added, 2 changed": only the kinds there are. */
  counts: string
}

/** The changes by family, in the order the API listed them. */
export function changeGroups(changes: readonly DesignSystemImportChangeDto[]): ChangeGroup[] {
  const families = [...new Set(changes.map(change => change.Family))]
  return families.map(family => {
    const ofFamily = changes.filter(change => change.Family === family)
    const counts = (['Added', 'Changed', 'Removed'] as const)
      .map(kind => [kind, ofFamily.filter(change => change.Kind === kind).length] as const)
      .filter(([, count]) => count > 0)
      .map(([kind, count]) => `${count} ${kind.toLowerCase()}`)
      .join(', ')
    return { family, changes: ofFamily, counts }
  })
}
